import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import {
  firebaseAuth,
  firebaseAuthReady,
  firebaseConfigured,
  githubProvider,
  googleProvider,
} from "../lib/firebase";

interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface AuthValue {
  user: User | null;
  loading: boolean;
  configured: boolean;
  register: (input: RegisterInput) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginGoogle: () => Promise<void>;
  loginGithub: () => Promise<void>;
  logout: () => Promise<void>;
  recover: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function requireAuth() {
  if (!firebaseAuth) throw new Error("THOT:El acceso todavía no está configurado.");
  return firebaseAuth;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(firebaseAuth?.currentUser ?? null);
  const [loading, setLoading] = useState(firebaseConfigured);

  useEffect(() => {
    if (!firebaseAuth) {
      setLoading(false);
      return;
    }
    const currentAuth = firebaseAuth;
    let unsubscribe = () => undefined;
    let cancelled = false;
    void firebaseAuthReady
      .then(() => {
        if (cancelled) return;
        unsubscribe = onAuthStateChanged(currentAuth, (next: User | null) => {
          setUser(next);
          setLoading(false);
          if (next) void api.bootstrap(next, {}).catch(() => undefined);
        });
      })
      .catch(() => setLoading(false));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      register: async ({ firstName, lastName, email, password }) => {
        await firebaseAuthReady;
        const credential = await createUserWithEmailAndPassword(requireAuth(), email, password);
        await updateProfile(credential.user, { displayName: `${firstName} ${lastName}`.trim() });
        await api.bootstrap(credential.user, { firstName, lastName });
        await sendEmailVerification(credential.user);
      },
      login: async (email, password) => {
        await firebaseAuthReady;
        const credential = await signInWithEmailAndPassword(requireAuth(), email, password);
        await api.bootstrap(credential.user, {});
      },
      loginGoogle: async () => {
        await firebaseAuthReady;
        const credential = await signInWithPopup(requireAuth(), googleProvider);
        await api.bootstrap(credential.user, {});
      },
      loginGithub: async () => {
        await firebaseAuthReady;
        const credential = await signInWithPopup(requireAuth(), githubProvider);
        await api.bootstrap(credential.user, {});
      },
      logout: () => signOut(requireAuth()),
      recover: async (email) => {
        await firebaseAuthReady;
        await sendPasswordResetEmail(requireAuth(), email);
      },
      resendVerification: async () => {
        const current = requireAuth().currentUser;
        if (!current) throw new Error("THOT:Ingresá para reenviar el email.");
        await sendEmailVerification(current);
      },
      refresh: async () => {
        const current = requireAuth().currentUser;
        if (!current) return;
        await current.reload();
        await current.getIdToken(true);
        setUser(requireAuth().currentUser);
      },
    }),
    [loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return value;
}
